import React, { useState, useEffect, useRef } from 'react';
import { Container, Row, Col, Card, Form, Button, Table, Alert, Badge, InputGroup } from 'react-bootstrap';
import { stockAPI } from '../utils/api';

const StockOuttake = () => {
  const [barcode, setBarcode] = useState('');
  const [quantity, setQuantity] = useState('1');
  const [reason, setReason] = useState('');
  const [note, setNote] = useState('');
  const [scannedMaterial, setScannedMaterial] = useState(null);
  const [recentTransactions, setRecentTransactions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [latestTransactionId, setLatestTransactionId] = useState(null);
  const [undoLoading, setUndoLoading] = useState(false);

  const barcodeInputRef = useRef(null);
  const focusDebounceRef = useRef(null);

  useEffect(() => {
    loadRecentTransactions();
    // Focus on barcode input on mount
    if (barcodeInputRef.current) {
      barcodeInputRef.current.focus();
    }
  }, []);

  const loadRecentTransactions = async () => {
    try {
      const res = await stockAPI.getTransactions({
        transactionType: 'outtake',
        limit: 10
      });
      setRecentTransactions(res.data.data);
    } catch (err) {
      console.error('Error loading transactions:', err);
    }
  };

  const handleBarcodeChange = (e) => {
    setBarcode(e.target.value);
    setError('');
    setScannedMaterial(null);
    // Clear latest transaction when starting a new scan
    setLatestTransactionId(null);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!barcode || !quantity) {
      setError('กรุณาระบุบาร์โค้ดและจำนวน');
      return;
    }

    if (!reason || !reason.trim()) {
      setError('กรุณาระบุเหตุผลในการเบิกสินค้าออก');
      return;
    }

    setLoading(true);
    setError('');
    setSuccess('');

    try {
      const res = await stockAPI.recordOuttake({
        barcode: barcode.trim(),
        quantity: parseFloat(quantity),
        reason: reason.trim(),
        note: note.trim()
      });

      setSuccess(`บันทึกสำเร็จ: ${res.data.data.material.name} -${res.data.data.quantityRemoved} ${res.data.data.material.unit}`);
      setScannedMaterial(res.data.data.material);

      // Store the transaction ID for undo functionality
      setLatestTransactionId(res.data.data.transactionId);

      // Reset form (keep reason and note for batch scanning)
      setBarcode('');
      setQuantity('1');

      // Reload transactions
      loadRecentTransactions();

      // Focus back to barcode input (with small delay to ensure DOM updates)
      setTimeout(() => {
        if (barcodeInputRef.current) {
          barcodeInputRef.current.focus();
        }
      }, 100);
    } catch (err) {
      setError(err.response?.data?.message || 'เกิดข้อผิดพลาดในการบันทึก');
      // Clear barcode and refocus on error
      setBarcode('');
      setTimeout(() => {
        if (barcodeInputRef.current) {
          barcodeInputRef.current.focus();
        }
      }, 100);
    } finally {
      setLoading(false);
    }
  };

  const handleUndo = async () => {
    if (!latestTransactionId) return;

    setUndoLoading(true);
    setError('');
    setSuccess('');

    try {
      const res = await stockAPI.undoTransaction(latestTransactionId);
      setSuccess(`ยกเลิกการบันทึกสำเร็จ: กลับสู่จำนวน ${res.data.data.restoredQuantity}`);
      setLatestTransactionId(null);
      setScannedMaterial(null);

      // Reload transactions
      loadRecentTransactions();

      // Focus back to barcode input
      setTimeout(() => {
        if (barcodeInputRef.current) {
          barcodeInputRef.current.focus();
        }
      }, 100);
    } catch (err) {
      setError(err.response?.data?.message || 'เกิดข้อผิดพลาดในการยกเลิก');
    } finally {
      setUndoLoading(false);
    }
  };

  const handleQuantityChange = (newQuantity) => {
    setQuantity(newQuantity);

    // Clear previous debounce timeout
    if (focusDebounceRef.current) {
      clearTimeout(focusDebounceRef.current);
    }

    // Set new timeout to focus on barcode input after user stops clicking
    focusDebounceRef.current = setTimeout(() => {
      if (barcodeInputRef.current) {
        barcodeInputRef.current.focus();
      }
    }, 300);
  };

  return (
    <Container fluid className="main-content">
      <Row className="mb-4">
        <Col>
          <h2>
            <i className="bi bi-box-arrow-right me-2"></i>
            เบิกสต็อกออก
          </h2>
          <p className="text-muted">สแกนบาร์โค้ดเพื่อบันทึกการเบิกสินค้าออกจากคลัง</p>
        </Col>
      </Row>

      {error && (
        <Alert variant="danger" dismissible onClose={() => setError('')}>
          {error}
        </Alert>
      )}

      {success && (
        <Alert variant="success" dismissible onClose={() => setSuccess('')}>
          {success}
        </Alert>
      )}

      <Row>
        <Col lg={6}>
          <Card className="mb-4">
            <Card.Header className="bg-danger text-white">
              <h5 className="mb-0">สแกนสินค้า</h5>
            </Card.Header>
            <Card.Body>
              <Form onSubmit={handleSubmit}>

                <Form.Group className="mb-3">
                  <Form.Label>
                    เหตุผล <span className="text-danger">*</span>
                  </Form.Label>
                  <Form.Control
                    type="text"
                    placeholder="เช่น เบิกเพื่อทดลอง เบิกเพื่อผลิต และสินค้าหมดอายุ"
                    value={reason}
                    onChange={(e) => setReason(e.target.value)}
                    disabled={loading}
                    required
                  />
                  <div className="mt-2 d-flex gap-2 flex-wrap">
                    <Button
                      variant="outline-secondary"
                      size="sm"
                      onClick={() => setReason('เบิกเพื่อทดลอง')}
                      disabled={loading}
                    >
                      เบิกเพื่อทดลอง
                    </Button>
                    <Button
                      variant="outline-secondary"
                      size="sm"
                      onClick={() => setReason('เบิกเพื่อผลิต')}
                      disabled={loading}
                    >
                      เบิกเพื่อผลิต
                    </Button>
                    <Button
                      variant="outline-secondary"
                      size="sm"
                      onClick={() => setReason('สินค้าหมดอายุ')}
                      disabled={loading}
                    >
                      สินค้าหมดอายุ
                    </Button>
                  </div>
                  <Form.Text className="text-muted">
                    ระบุเหตุผลในการเบิกสินค้าออก (บังคับ) หรือคลิกปุ่มด้านบนเพื่อเลือก
                  </Form.Text>
                </Form.Group>

                <Form.Group className="mb-3">
                  <Form.Label>หมายเหตุ (ไม่บังคับ)</Form.Label>
                  <Form.Control
                    as="textarea"
                    rows={2}
                    placeholder="หมายเหตุเพิ่มเติม..."
                    value={note}
                    onChange={(e) => setNote(e.target.value)}
                    disabled={loading}
                  />
                </Form.Group>

                <Form.Group className="mb-3">
                  <Form.Label>จำนวน</Form.Label>
                  <InputGroup>
                    <Button
                      variant="outline-secondary"
                      onClick={() => handleQuantityChange(Math.max(1, parseFloat(quantity) - 1).toString())}
                      disabled={loading || parseFloat(quantity) <= 1}
                    >
                      <i className="bi bi-dash"></i>
                    </Button>
                    <Form.Control
                      type="number"
                      step="1"
                      min="1"
                      placeholder="ระบุจำนวน..."
                      value={quantity}
                      onChange={(e) => setQuantity(e.target.value)}
                      disabled={loading}
                      required
                      className="text-center"
                    />
                    <Button
                      variant="outline-secondary"
                      onClick={() => handleQuantityChange((parseFloat(quantity) + 1).toString())}
                      disabled={loading}
                    >
                      <i className="bi bi-plus"></i>
                    </Button>
                  </InputGroup>
                </Form.Group>

                <Form.Group className="mb-3">
                  <Form.Label>บาร์โค้ด</Form.Label>
                  <Form.Control
                    ref={barcodeInputRef}
                    type="text"
                    placeholder="สแกนหรือพิมพ์บาร์โค้ด..."
                    value={barcode}
                    onChange={handleBarcodeChange}
                    autoFocus
                    disabled={loading}
                  />
                  <Form.Text className="text-muted">
                    ใช้เครื่องสแกนบาร์โค้ดหรือพิมพ์ด้วยมือ
                  </Form.Text>
                </Form.Group>

                <div className="d-grid gap-2">
                  <Button
                    variant="danger"
                    type="submit"
                    disabled={loading || !barcode || !quantity || !reason || undoLoading}
                  >
                    {loading ? (
                      <>
                        <span className="spinner-border spinner-border-sm me-2"></span>
                        กำลังบันทึก...
                      </>
                    ) : (
                      <>
                        <i className="bi bi-dash-circle me-2"></i>
                        บันทึกการเบิกออก
                      </>
                    )}
                  </Button>

                  {latestTransactionId && (
                    <Button
                      variant="warning"
                      onClick={handleUndo}
                      disabled={undoLoading || loading}
                    >
                      {undoLoading ? (
                        <>
                          <span className="spinner-border spinner-border-sm me-2"></span>
                          กำลังยกเลิก...
                        </>
                      ) : (
                        <>
                          <i className="bi bi-arrow-counterclockwise me-2"></i>
                          ยกเลิกการบันทึกล่าสุด
                        </>
                      )}
                    </Button>
                  )}
                </div>
              </Form>

              {scannedMaterial && (
                <Alert variant="warning" className="mt-3 mb-0">
                  <strong>สินค้าที่บันทึก:</strong><br/>
                  {scannedMaterial.code} - {scannedMaterial.name}<br/>
                  <strong>สต็อกคงเหลือ:</strong> {scannedMaterial.quantityOnHand} {scannedMaterial.unit}
                </Alert>
              )}
            </Card.Body>
          </Card>
        </Col>

        <Col lg={6}>
          <Card>
            <Card.Header>
              <h5 className="mb-0">การบันทึกล่าสุด</h5>
            </Card.Header>
            <Card.Body className="p-0">
              {recentTransactions.length === 0 ? (
                <div className="text-center py-4">
                  <p className="text-muted">ยังไม่มีการบันทึก</p>
                </div>
              ) : (
                <Table responsive hover className="mb-0">
                  <thead className="table-light">
                    <tr>
                      <th>เวลา</th>
                      <th>สินค้า</th>
                      <th>จำนวน</th>
                      <th>เหตุผล</th>
                    </tr>
                  </thead>
                  <tbody>
                    {recentTransactions.map((txn) => (
                      <tr key={txn.id}>
                        <td>
                          <small>
                            {new Date(txn.createdAt).toLocaleString('th-TH')}
                          </small>
                        </td>
                        <td>
                          <strong>{txn.material.name}</strong><br/>
                          <small className="text-muted">{txn.material.code}</small>
                        </td>
                        <td>
                          <Badge bg="danger">
                            {txn.quantity} {txn.material.unit}
                          </Badge>
                        </td>
                        <td>
                          <small>{txn.reference || '-'}</small>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
              )}
            </Card.Body>
          </Card>
        </Col>
      </Row>
    </Container>
  );
};

export default StockOuttake;
